import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';

const faqItems = [
  {
    question: "What is included in the Annual Maintenance Contract?",
    answer: "Our Annual Maintenance Contract (AMC) includes regular preventive maintenance, priority support, hardware repairs, software updates, security patches, performance optimization, and detailed reporting. The specific details can be customized based on your business requirements."
  },
  {
    question: "How quickly can I expect a response when I report an issue?",
    answer: "Our standard response time is within 4 hours for critical issues and 8 hours for non-critical issues. For clients with premium support plans, we offer faster response times of 1-2 hours for critical issues."
  },
  {
    question: "Do you provide on-site support or only remote assistance?",
    answer: "We provide both remote and on-site support depending on the nature of the issue. Many problems can be resolved quickly through our remote support system, but for hardware-related issues or complex problems, our technicians will visit your premises."
  },
  {
    question: "Can you support hardware from different manufacturers?",
    answer: "Yes, we offer multi-brand hardware support. Our technicians are trained to work with equipment from major manufacturers like HP, Dell, Lenovo, Cisco, Apple, and many others."
  },
  {
    question: "How do you handle data security during maintenance?",
    answer: "Data security is our top priority. We follow strict security protocols during maintenance activities, including secure access methods, data encryption, and confidentiality agreements. Our technicians are trained in data protection best practices."
  },
  {
    question: "Can you create a custom support plan for our specific needs?",
    answer: "Absolutely! We understand that every business has unique requirements. Our team can work with you to develop a customized support plan that addresses your specific needs, concerns, and budget constraints."
  }
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  
  const toggleItem = (index) => {
    if (activeIndex === index) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };
  
  return (
    <section className="py-20 bg-gray-50">
      <Container>
        <SectionHeading
          title="Frequently Asked Questions"
          subtitle="Find answers to common questions about our services."
          center
        />
        
        <div className="max-w-3xl mx-auto mt-12">
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`mb-4 rounded-lg overflow-hidden ${
                activeIndex === index ? 'border-blue-200 bg-white shadow-md' : 'border border-gray-200 bg-white'
              }`}
            >
              <button
                className="w-full text-left p-4 md:p-6 flex justify-between items-center focus:outline-none"
                onClick={() => toggleItem(index)}
              >
                <span className="font-semibold text-lg text-gray-800">{item.question}</span>
                <span className="text-blue-600 flex-shrink-0 ml-4">
                  {activeIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </span>
              </button>
              
              {activeIndex === index && (
                <div className="px-4 md:px-6 pb-4 md:pb-6 text-gray-600">
                  <div className="border-t border-gray-100 pt-4">
                    {item.answer}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Don't see your question here? <a href="/contact" className="text-blue-600 font-medium hover:underline">Contact us</a> for more information.
          </p>
        </div>
      </Container>
    </section>
  );
};

export default FAQ;